"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.wordpressActions = exports.WORDPRESS_BG = exports.WORDPRESS_COLOR = exports.WORDPRESS_CATEGORY = exports.wordpressActionsArgsSchema = exports.WordPressAction = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// WordPress block schema (used by getPage)
var wordpressBlockSchema = zod_1.z.lazy(function () {
    return zod_1.z.object({
        blockName: zod_1.z
            .string()
            .nullable()
            .describe('Block type (e.g., core/paragraph) or null for freeform HTML'),
        attrs: zod_1.z.record(zod_1.z.any()).describe('Block attributes'),
        innerHTML: zod_1.z.string().describe('Block inner HTML content'),
        innerContent: zod_1.z.array(zod_1.z.union([zod_1.z.string(), zod_1.z.null()])).describe('Block inner content array'),
        innerBlocks: zod_1.z.array(wordpressBlockSchema).describe('Nested blocks'),
    });
});
// Section schemas (used by publishPage and updatePage)
var headerSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('header'),
    level: zod_1.z.number().min(1).max(6),
    content: zod_1.z.string(),
    id: zod_1.z.string().nullish(),
    keywords: zod_1.z.array(zod_1.z.string()).nullish(),
});
var paragraphSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('paragraph'),
    content: zod_1.z.string(),
    id: zod_1.z.string().nullish(),
});
var imageSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('image'),
    src: zod_1.z.string().url(),
    alt: zod_1.z.string().max(125),
    title: zod_1.z.string().nullish(),
    caption: zod_1.z.string().nullish(),
    width: zod_1.z.number().min(1).nullish(),
    height: zod_1.z.number().min(1).nullish(),
    formats: zod_1.z
        .object({
        webp: zod_1.z.string().url().nullish(),
        avif: zod_1.z.string().url().nullish(),
    })
        .nullish(),
});
var listSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('list'),
    style: zod_1.z.enum(['ordered', 'unordered']),
    items: zod_1.z.array(zod_1.z.string()),
});
var tableSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('table'),
    caption: zod_1.z.string().nullish(),
    headers: zod_1.z.array(zod_1.z.string()),
    rows: zod_1.z.array(zod_1.z.array(zod_1.z.string())),
});
var faqSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('faq'),
    items: zod_1.z.array(zod_1.z.object({
        question: zod_1.z.string(),
        answer: zod_1.z.string(),
        id: zod_1.z.string().nullish(),
    })),
});
var codeSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('code'),
    language: zod_1.z.string().nullish(),
    content: zod_1.z.string(),
    caption: zod_1.z.string().nullish(),
});
var blockquoteSectionSchema = zod_1.z.object({
    type: zod_1.z.literal('blockquote'),
    content: zod_1.z.string(),
    citation: zod_1.z.string().nullish(),
    url: zod_1.z.string().url().nullish(),
});
var contentSectionSchema = zod_1.z.union([
    headerSectionSchema,
    paragraphSectionSchema,
    imageSectionSchema,
    listSectionSchema,
    tableSectionSchema,
    faqSectionSchema,
    codeSectionSchema,
    blockquoteSectionSchema,
]);
// WordPress-specific metadata for content changes
var wordpressContentChangeSchema = common_1.contentChangeSchema.extend({
    changeReason: zod_1.z.string().nullish().describe('Explanation of why this change improves SEO'),
    impactLevel: zod_1.z.enum(['low', 'medium', 'high']).nullish().describe('SEO impact level'),
    keywordsTargeted: zod_1.z.array(zod_1.z.string()).nullish().describe('Keywords this change targets'),
});
// ---------- Content Input Schemas (shared) ----------
// Discriminated union for content input
var markdownContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('markdown'),
    content: zod_1.z
        .string()
        .describe('The page content (Markdown format) - will be converted to WordPress blocks'),
});
var blocksContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('blocks'),
    content: zod_1.z
        .string()
        .describe('Raw WordPress Gutenberg blocks obtained from getContent action - DO NOT generate blocks manually, always fetch existing blocks first and modify them'),
});
var sectionsContentSchema = zod_1.z.object({
    contentType: zod_1.z.literal('sections'),
    sections: zod_1.z
        .array(contentSectionSchema)
        .min(1)
        .describe('Structured content sections that generate proper WordPress Gutenberg blocks (compatible with seoArticleWriter output)'),
});
// ---------- Content Update Schemas (shared) ----------
// Discriminated union for content update types (mutually exclusive)
var blocksContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('blocks'),
    content: zod_1.z
        .string()
        .describe('Replace entire page content with raw WordPress Gutenberg blocks obtained from getContent - DO NOT generate blocks manually'),
});
var sectionsContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('sections'),
    sections: zod_1.z
        .array(contentSectionSchema)
        .min(1)
        .describe('Replace entire page content with structured sections that generate proper WordPress Gutenberg blocks (compatible with seoArticleWriter)'),
});
var changesContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('changes'),
    changes: zod_1.z
        .array(wordpressContentChangeSchema)
        .min(1)
        .describe('Apply incremental changes to existing content (compatible with seoArticleUpdater)'),
});
var noContentUpdate = zod_1.z.object({
    updateType: zod_1.z.literal('none'),
});
// ==========================
// Unified Content Actions (Pages & Posts)
// ==========================
// ---------- Get Content ----------
var wordpressActionsGetContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    zod_1.z.object({
        contentType: zod_1.z.literal('page'),
        contentUrl: zod_1.z.string().describe('The URL or ID of the WordPress page to fetch'),
    }),
    zod_1.z.object({
        contentType: zod_1.z.literal('post'),
        contentUrl: zod_1.z.string().describe('The URL or ID of the WordPress post to fetch'),
    }),
]);
// Result schemas for different content types
var wordpressActionsGetPageContentResultSchema = zod_1.z.object({
    contentType: zod_1.z.literal('page'),
    id: zod_1.z.number().describe('The WordPress page ID'),
    url: zod_1.z.string().describe('The public URL of the page'),
    title: zod_1.z.string().describe('The page title'),
    contentBlocks: zod_1.z.array(wordpressBlockSchema).describe('Parsed Gutenberg block structure'),
    contentHtml: zod_1.z.string().describe('The page content in HTML format'),
    excerpt: zod_1.z.string().nullish().describe('Optional brief description of the page'),
    status: zod_1.z.string().describe('The page status (publish, draft, pending, or private)'),
    slug: zod_1.z.string().describe('The URL-friendly slug'),
    author: zod_1.z.number().describe('The author user ID'),
    modified: zod_1.z.string().describe('The last modified date (ISO 8601 format)'),
});
var wordpressActionsGetPostContentResultSchema = zod_1.z.object({
    contentType: zod_1.z.literal('post'),
    id: zod_1.z.number().describe('The WordPress post ID'),
    url: zod_1.z.string().describe('The public URL of the post'),
    title: zod_1.z.string().describe('The post title'),
    contentBlocks: zod_1.z.array(wordpressBlockSchema).describe('Parsed Gutenberg block structure'),
    contentHtml: zod_1.z.string().describe('The post content in HTML format'),
    excerpt: zod_1.z.string().nullish().describe('Optional brief excerpt of the post'),
    status: zod_1.z.string().describe('The post status (publish, draft, pending, or private)'),
    slug: zod_1.z.string().describe('The URL-friendly slug'),
    categories: zod_1.z.array(zod_1.z.number()).describe('Array of category IDs assigned to the post'),
    tags: zod_1.z.array(zod_1.z.number()).describe('Array of tag IDs assigned to the post'),
    featuredMediaId: zod_1.z.number().nullish().describe('The featured image media ID'),
    author: zod_1.z.number().describe('The author user ID'),
    modified: zod_1.z.string().describe('The last modified date (ISO 8601 format)'),
});
var wordpressActionsGetContentResultSchema = zod_1.z.union([
    wordpressActionsGetPageContentResultSchema,
    wordpressActionsGetPostContentResultSchema,
]);
// ---------- List Content ----------
// Base schema for common list filters
var baseListContentSchema = zod_1.z.object({
    status: zod_1.z
        .enum(['publish', 'draft', 'pending', 'private', 'any'])
        .nullish()
        .describe('Filter by status'),
    search: zod_1.z.string().nullish().describe('Search query to filter content'),
    perPage: zod_1.z.number().min(1).max(100).default(10).describe('Number of items per page (1-100)'),
    page: zod_1.z.number().min(1).default(1).describe('Page number for pagination'),
    order: zod_1.z.enum(['asc', 'desc']).default('desc').describe('Sort order'),
});
// Page-specific list filters
var listPagesSchema = baseListContentSchema.extend({
    contentType: zod_1.z.literal('page'),
    parent: zod_1.z.number().nullish().describe('Filter by parent page ID'),
    orderBy: zod_1.z
        .enum(['date', 'modified', 'title', 'menu_order'])
        .default('date')
        .describe('Sort pages by this field'),
});
// Post-specific list filters
var listPostsSchema = baseListContentSchema.extend({
    contentType: zod_1.z.literal('post'),
    categories: zod_1.z.array(zod_1.z.number()).nullish().describe('Filter by category IDs'),
    tags: zod_1.z.array(zod_1.z.number()).nullish().describe('Filter by tag IDs'),
    author: zod_1.z.number().nullish().describe('Filter by author user ID'),
    orderBy: zod_1.z
        .enum(['date', 'modified', 'title', 'relevance'])
        .default('date')
        .describe('Sort posts by this field'),
});
var wordpressActionsListContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    listPagesSchema,
    listPostsSchema,
]);
// List result schemas
var wordpressActionsListPagesResultSchema = zod_1.z.object({
    contentType: zod_1.z.literal('page'),
    items: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.number(),
        url: zod_1.z.string(),
        title: zod_1.z.string(),
        excerpt: zod_1.z.string(),
        status: zod_1.z.string(),
        slug: zod_1.z.string(),
        author: zod_1.z.number(),
        parent: zod_1.z.number(),
        menuOrder: zod_1.z.number(),
        date: zod_1.z.string(),
        modified: zod_1.z.string(),
    })),
    total: zod_1.z.number().describe('Total number of pages matching criteria'),
    totalPages: zod_1.z.number().describe('Total number of pages'),
});
var wordpressActionsListPostsResultSchema = zod_1.z.object({
    contentType: zod_1.z.literal('post'),
    items: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.number(),
        url: zod_1.z.string(),
        title: zod_1.z.string(),
        excerpt: zod_1.z.string(),
        status: zod_1.z.string(),
        slug: zod_1.z.string(),
        author: zod_1.z.number(),
        categories: zod_1.z.array(zod_1.z.number()),
        tags: zod_1.z.array(zod_1.z.number()),
        featuredMediaId: zod_1.z.number().nullish(),
        date: zod_1.z.string(),
        modified: zod_1.z.string(),
    })),
    total: zod_1.z.number().describe('Total number of posts matching criteria'),
    totalPages: zod_1.z.number().describe('Total number of pages'),
});
var wordpressActionsListContentResultSchema = zod_1.z.union([
    wordpressActionsListPagesResultSchema,
    wordpressActionsListPostsResultSchema,
]);
// ---------- Publish Content ----------
// Base schema for common publish fields
var basePublishContentSchema = zod_1.z.object({
    title: zod_1.z.string().describe('The content title'),
    contentInput: zod_1.z
        .discriminatedUnion('contentType', [
        markdownContentSchema,
        blocksContentSchema,
        sectionsContentSchema,
    ])
        .describe('Content format: markdown (will convert), blocks (from getContent), or sections (structured data). For content modifications, prefer using performTask action.'),
    status: zod_1.z
        .enum(['publish', 'draft', 'pending', 'private'])
        .default('publish')
        .describe('The content status'),
    slug: zod_1.z.string().nullish().describe('Optional URL-friendly slug'),
    excerpt: zod_1.z.string().nullish().describe('Optional brief excerpt or description'),
    featuredMediaId: zod_1.z.number().nullish().describe('Optional WordPress media ID for featured image'),
    commentStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional comment status (open or closed)'),
    pingStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional ping/trackback status (open or closed)'),
});
// Page-specific publish fields
var publishPageSchema = basePublishContentSchema.extend({
    contentType: zod_1.z.literal('page'),
    parentId: zod_1.z.number().nullish().describe('Optional parent page ID for hierarchical pages'),
    menuOrder: zod_1.z.number().nullish().describe('Optional menu order for the page'),
});
// Post-specific publish fields
var publishPostSchema = basePublishContentSchema.extend({
    contentType: zod_1.z.literal('post'),
    categories: zod_1.z.array(zod_1.z.number()).nullish().describe('Array of category IDs to assign to the post'),
    tags: zod_1.z.array(zod_1.z.number()).nullish().describe('Array of tag IDs to assign to the post'),
});
var wordpressActionsPublishContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    publishPageSchema,
    publishPostSchema,
]);
var wordpressActionsPublishContentResultSchema = zod_1.z.object({
    id: zod_1.z.number().describe('The WordPress content ID'),
    url: zod_1.z.string().describe('The public URL of the published content'),
    status: zod_1.z.string().describe('The content status'),
    title: zod_1.z.string().describe('The content title'),
    slug: zod_1.z.string().describe('The URL-friendly slug'),
});
// ---------- Update Content ----------
// Base schema for common update fields
var baseUpdateContentSchema = zod_1.z.object({
    contentUrl: zod_1.z.string().describe('The URL or ID of the WordPress content to update'),
    title: zod_1.z.string().nullish().describe('Optional new title'),
    contentUpdate: zod_1.z
        .discriminatedUnion('updateType', [
        blocksContentUpdate,
        sectionsContentUpdate,
        changesContentUpdate,
        noContentUpdate,
    ])
        .nullish()
        .describe('How to update content: blocks (from getContent), sections (structured), changes (incremental), or none. For complex modifications, prefer using performTask action.'),
    status: zod_1.z
        .enum(['publish', 'draft', 'pending', 'private'])
        .nullish()
        .describe('Optional new status'),
    slug: zod_1.z.string().nullish().describe('Optional new URL-friendly slug'),
    excerpt: zod_1.z.string().nullish().describe('Optional new brief excerpt or description'),
    featuredMediaId: zod_1.z.number().nullish().describe('Optional WordPress media ID for featured image'),
    commentStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional comment status (open or closed)'),
    pingStatus: zod_1.z
        .enum(['open', 'closed'])
        .nullish()
        .describe('Optional ping/trackback status (open or closed)'),
});
// Page-specific update fields
var updatePageSchema = baseUpdateContentSchema.extend({
    contentType: zod_1.z.literal('page'),
    parentId: zod_1.z.number().nullish().describe('Optional parent page ID for hierarchical pages'),
    menuOrder: zod_1.z.number().nullish().describe('Optional menu order for the page'),
});
// Post-specific update fields
var updatePostSchema = baseUpdateContentSchema.extend({
    contentType: zod_1.z.literal('post'),
    categories: zod_1.z
        .array(zod_1.z.number())
        .nullish()
        .describe('Array of category IDs to assign (replaces existing)'),
    tags: zod_1.z.array(zod_1.z.number()).nullish().describe('Array of tag IDs to assign (replaces existing)'),
});
var wordpressActionsUpdateContentArgsSchema = zod_1.z.discriminatedUnion('contentType', [
    updatePageSchema,
    updatePostSchema,
]);
var wordpressActionsUpdateContentResultSchema = zod_1.z.object({
    id: zod_1.z.number().describe('The WordPress content ID'),
    url: zod_1.z.string().describe('The public URL of the updated content'),
    status: zod_1.z.string().describe('The content status'),
    title: zod_1.z.string().describe('The content title'),
    slug: zod_1.z.string().describe('The URL-friendly slug'),
    modified: zod_1.z.string().describe('The last modified date (ISO 8601 format)'),
});
// ---------- Upload Media ----------
var wordpressActionsUploadMediaArgsSchema = zod_1.z.object({
    fileUrl: zod_1.z.string().url().describe('URL of the file to upload to WordPress media library'),
    fileName: zod_1.z
        .string()
        .nullish()
        .describe('Optional filename (will be extracted from URL if omitted)'),
    altText: zod_1.z.string().nullish().describe('Optional alt text for accessibility and SEO'),
    title: zod_1.z.string().nullish().describe('Optional media title'),
    caption: zod_1.z.string().nullish().describe('Optional media caption'),
    description: zod_1.z.string().nullish().describe('Optional media description'),
});
var wordpressActionsUploadMediaResultSchema = zod_1.z.object({
    id: zod_1.z.number().describe('The WordPress media ID'),
    url: zod_1.z.string().describe('The public URL of the uploaded media'),
    sourceUrl: zod_1.z.string().describe('Direct URL to the media file'),
    mimeType: zod_1.z.string().describe('MIME type of the uploaded file'),
    width: zod_1.z.number().nullish().describe('Image width in pixels (if applicable)'),
    height: zod_1.z.number().nullish().describe('Image height in pixels (if applicable)'),
});
// ---------- Get Media ----------
var wordpressActionsGetMediaArgsSchema = zod_1.z.object({
    mediaId: zod_1.z.number().describe('The WordPress media ID'),
});
var wordpressActionsGetMediaResultSchema = zod_1.z.object({
    id: zod_1.z.number().describe('The WordPress media ID'),
    url: zod_1.z.string().describe('The public URL of the media'),
    sourceUrl: zod_1.z.string().describe('Direct URL to the media file'),
    title: zod_1.z.string().describe('Media title'),
    altText: zod_1.z.string().nullish().describe('Alt text for accessibility'),
    caption: zod_1.z.string().nullish().describe('Media caption'),
    description: zod_1.z.string().nullish().describe('Media description'),
    mimeType: zod_1.z.string().describe('MIME type of the file'),
    width: zod_1.z.number().nullish().describe('Image width in pixels (if applicable)'),
    height: zod_1.z.number().nullish().describe('Image height in pixels (if applicable)'),
});
// ---------- Update Media ----------
var wordpressActionsUpdateMediaArgsSchema = zod_1.z.object({
    mediaId: zod_1.z.number().describe('The WordPress media ID'),
    altText: zod_1.z.string().nullish().describe('New alt text for accessibility and SEO'),
    title: zod_1.z.string().nullish().describe('New media title'),
    caption: zod_1.z.string().nullish().describe('New media caption'),
    description: zod_1.z.string().nullish().describe('New media description'),
});
var wordpressActionsUpdateMediaResultSchema = zod_1.z.object({
    id: zod_1.z.number().describe('The WordPress media ID'),
    url: zod_1.z.string().describe('The public URL of the media'),
    updated: zod_1.z.boolean().describe('Whether the update was successful'),
});
// ---------- List Categories ----------
var wordpressActionsListCategoriesArgsSchema = zod_1.z.object({
    search: zod_1.z.string().nullish().describe('Search query to filter categories'),
    perPage: zod_1.z.number().min(1).max(100).default(100).describe('Number of categories per page'),
});
var wordpressActionsListCategoriesResultSchema = zod_1.z.object({
    categories: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.number(),
        name: zod_1.z.string(),
        slug: zod_1.z.string(),
        description: zod_1.z.string(),
        count: zod_1.z.number().describe('Number of posts in this category'),
        parent: zod_1.z.number().nullish().describe('Parent category ID (if hierarchical)'),
    })),
    total: zod_1.z.number(),
});
// ---------- List Tags ----------
var wordpressActionsListTagsArgsSchema = zod_1.z.object({
    search: zod_1.z.string().nullish().describe('Search query to filter tags'),
    perPage: zod_1.z.number().min(1).max(100).default(100).describe('Number of tags per page'),
});
var wordpressActionsListTagsResultSchema = zod_1.z.object({
    tags: zod_1.z.array(zod_1.z.object({
        id: zod_1.z.number(),
        name: zod_1.z.string(),
        slug: zod_1.z.string(),
        description: zod_1.z.string(),
        count: zod_1.z.number().describe('Number of posts with this tag'),
    })),
    total: zod_1.z.number(),
});
// ---------- Perform Task ----------
var wordpressActionsPerformTaskArgsSchema = zod_1.z.object({
    task: zod_1.z.string().describe("Natural language description of task to perform on the WordPress site. Should include all necessary details about what needs to be done, which content to target, what changes to make, etc.\n\nExamples:\n- \"Replace the hero text on the homepage with: [content]\"\n- \"Update all images in the About page to have proper alt text for SEO\"\n- \"Find the latest blog post about AI and add it to the Technology category\"\n- \"Update the featured image on the Contact page\"\n\nIMPORTANT:\nMake sure you include all the necessary details required to perform the task. For example:\n- If you need to update content, include the page/post URL or ID, and exactly what content you want to change and in what way.\n- If you need to upload media, include the image URL and desired metadata.\n\nThis agent is not good at writing content, so pass in the content you want to use, and describe how you want to use it."),
});
var wordpressActionsPerformTaskResultSchema = zod_1.z.object({
    url: zod_1.z.string().nullish().describe('URL or domain of the WordPress site'),
    summary: zod_1.z.string().describe('Summary of what was accomplished'),
    changes: zod_1.z
        .array(zod_1.z.object({
        action: zod_1.z.string().describe('Action taken'),
        target: zod_1.z.string().describe('What was targeted (page, post, media, etc.)'),
        details: zod_1.z.string().describe('Specific details about the change'),
    }))
        .describe('List of changes made'),
});
// ==========================
// Compound Tool: wordpressActions
// ==========================
exports.WordPressAction = zod_1.z.enum([
    // Content actions
    'getContent',
    'listContent',
    'publishContent',
    'updateContent',
    // Media actions
    'uploadMedia',
    'getMedia',
    'updateMedia',
    // Taxonomy actions
    'listCategories',
    'listTags',
    // Agentic task performer
    'performTask',
]);
exports.wordpressActionsArgsSchema = zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        // Content actions
        zod_1.z.object({ name: zod_1.z.literal('getContent'), params: wordpressActionsGetContentArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('listContent'), params: wordpressActionsListContentArgsSchema }),
        zod_1.z.object({
            name: zod_1.z.literal('publishContent'),
            params: wordpressActionsPublishContentArgsSchema,
        }),
        zod_1.z.object({
            name: zod_1.z.literal('updateContent'),
            params: wordpressActionsUpdateContentArgsSchema,
        }),
        // Media actions
        zod_1.z.object({ name: zod_1.z.literal('uploadMedia'), params: wordpressActionsUploadMediaArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('getMedia'), params: wordpressActionsGetMediaArgsSchema }),
        zod_1.z.object({ name: zod_1.z.literal('updateMedia'), params: wordpressActionsUpdateMediaArgsSchema }),
        // Taxonomy actions
        zod_1.z.object({
            name: zod_1.z.literal('listCategories'),
            params: wordpressActionsListCategoriesArgsSchema,
        }),
        zod_1.z.object({ name: zod_1.z.literal('listTags'), params: wordpressActionsListTagsArgsSchema }),
        // Agentic task performer
        zod_1.z
            .object({ name: zod_1.z.literal('performTask'), params: wordpressActionsPerformTaskArgsSchema })
            .describe('RECOMMENDED for content modifications - uses specialized WordPress agent with tools for searching, updating blocks, and finding/replacing text. Handles complex edits better than raw block manipulation.'),
    ]),
    emitMode: zod_1.z.literal('single'),
});
// Union of all possible result shapes
var WordPressActionsAllResultsSchema = zod_1.z.union([
    // Content results
    wordpressActionsGetContentResultSchema,
    wordpressActionsListContentResultSchema,
    wordpressActionsPublishContentResultSchema,
    wordpressActionsUpdateContentResultSchema,
    // Media results
    wordpressActionsUploadMediaResultSchema,
    wordpressActionsGetMediaResultSchema,
    wordpressActionsUpdateMediaResultSchema,
    // Taxonomy results
    wordpressActionsListCategoriesResultSchema,
    wordpressActionsListTagsResultSchema,
    // Task performer result
    wordpressActionsPerformTaskResultSchema,
]);
// Dynamic result schemas keyed by action
var wordpressActionsResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['action.name'],
    variants: [
        // Content actions
        { when: { 'action.name': 'getContent' }, schema: wordpressActionsGetContentResultSchema },
        { when: { 'action.name': 'listContent' }, schema: wordpressActionsListContentResultSchema },
        {
            when: { 'action.name': 'publishContent' },
            schema: wordpressActionsPublishContentResultSchema,
        },
        {
            when: { 'action.name': 'updateContent' },
            schema: wordpressActionsUpdateContentResultSchema,
        },
        // Media actions
        { when: { 'action.name': 'uploadMedia' }, schema: wordpressActionsUploadMediaResultSchema },
        { when: { 'action.name': 'getMedia' }, schema: wordpressActionsGetMediaResultSchema },
        { when: { 'action.name': 'updateMedia' }, schema: wordpressActionsUpdateMediaResultSchema },
        // Taxonomy actions
        {
            when: { 'action.name': 'listCategories' },
            schema: wordpressActionsListCategoriesResultSchema,
        },
        { when: { 'action.name': 'listTags' }, schema: wordpressActionsListTagsResultSchema },
        // Task performer
        { when: { 'action.name': 'performTask' }, schema: wordpressActionsPerformTaskResultSchema },
    ],
    defaultSchema: WordPressActionsAllResultsSchema,
});
exports.WORDPRESS_CATEGORY = common_1.ToolCategorySchema.enum.communication;
exports.WORDPRESS_COLOR = common_1.BRAND_COLORS.WORDPRESS;
exports.WORDPRESS_BG = (0, common_1.createBgColor)(exports.WORDPRESS_COLOR);
// Compound tool metadata
exports.wordpressActions = (0, common_1.createToolMetadata)({
    id: 'wordpressActions',
    displayName: 'WordPress',
    displayDescription: 'Manage WordPress pages, posts, media, and taxonomy. For content modifications, prefer using performTask action which provides specialized WordPress tools and context.',
    category: exports.WORDPRESS_CATEGORY,
    color: exports.WORDPRESS_COLOR,
    backgroundColor: exports.WORDPRESS_BG,
    icon: 'wordpress',
    argsSchema: exports.wordpressActionsArgsSchema,
}, wordpressActionsResultSchemas);
